import { useAngularContext } from 'AngularContext';
import { hasCompletedFoundations } from 'ContentProgress/utils/playlistProgressUtils';
import { memo, useEffect, useMemo, useState } from 'react';
import { Text } from 'Text';
import { useTranslation } from 'react-i18next';
import { SidebarBoxKey } from 'AdmissionsGuidance/StudentDashboard.types';
import { useCurrentUser } from 'FrontRoyalAngular';
import { ProgramTypeConfigs } from 'Program';
import { getCohort } from 'Users';
import { LogoBox } from '../../LogoBox';
import { FoundationsProgress } from './FoundationsProgress';

function ApplicationReceivedBoxComponent() {
    const [foundationsComplete, setFoundationsComplete] = useState(false);
    const $injector = useAngularContext();
    const { t } = useTranslation('back_royal', { keyPrefix: 'applicationReceivedBox' });
    const currentUser = useCurrentUser();
    const cohort = useMemo(() => getCohort(currentUser), [currentUser]);
    const playlistTitleKey = useMemo(() => {
        if (!cohort) return null;
        return ProgramTypeConfigs[cohort.programType].applicationReceivedBoxPlaylistTitleKey;
    }, [cohort]);
    const content = useMemo(() => {
        if (foundationsComplete) return t('descriptionFoundationsComplete');
        if (!playlistTitleKey) return null;
        return t('description', { playlistTitle: t(playlistTitleKey) });
    }, [playlistTitleKey, t, foundationsComplete]);

    useEffect(() => {
        hasCompletedFoundations($injector).then(setFoundationsComplete);
    }, [$injector]);

    if (!content) return null;

    return (
        <ApplicationReceivedBoxPresentation
            content={content}
            foundationsComplete={foundationsComplete}
            playlistTitle={t(playlistTitleKey!)}
        />
    );
}

function ApplicationReceivedBoxPresentation({
    content,
    foundationsComplete,
    playlistTitle,
}: {
    content: string;
    foundationsComplete: boolean;
    playlistTitle: string;
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'applicationReceivedBox' });
    return (
        <LogoBox title={t('title')} boxKey={SidebarBoxKey.application_received}>
            <Text className="mb-5">{content}</Text>
            <FoundationsProgress foundationsComplete={foundationsComplete} playlistTitle={playlistTitle} />
        </LogoBox>
    );
}

export const ApplicationReceivedBox = memo(ApplicationReceivedBoxComponent);
